import { History } from 'history';
import _ from 'lodash';

import { getCurrentLocation } from './HistoryProvider';
import * as DevOps from '../devOpsHelper/DevOps';

const WHITE_LISTED_PARAMS = ['overlay', 'state_key'];

export function getQueryParameter(
    location: Location | History['location'],
    queryParameter: string
) {
    const regExp = new RegExp('[?&]' + queryParameter + '=([^&#]*)', 'i');
    if (!_.get(location, 'search')) return;
    const match = regExp.exec(location.search!);

    if (match) {
        return match[1];
    }
}

export function redirectToLogin() {
    if (!DevOps.isStorybookEnv()) {
        if (window.location.pathname === '/account/link-open-banking/') {
            window.location.assign(
                `/login/?redirect_to=${encodeURIComponent(
                    '/account/link-open-banking/'
                )}`
            );
        } else if (
            window.location.pathname === '/account/credit-card/consent-review/'
        ) {
            window.location.assign(
                `/login/?persist_params=1&redirect_to=${encodeURIComponent(
                    window.location.pathname + window.location.search
                )}`
            );
        } else {
            window.location.assign(`/login/`);
        }
    }
}

export function isLinkingOpenBankingOnly() {
    return window.location.pathname === '/account/link-open-banking/';
}

export function addQueryParameterInLocation(
    currentLocation: Location,
    queryParameterName: string,
    value: string
) {
    const search = updateQueryStringParameter(
        currentLocation && currentLocation.search,
        queryParameterName,
        value
    );

    return {
        ...currentLocation,
        search
    };
}

export function removeQueryParameterInLocation(
    currentLocation: Location,
    queryParameterName: string
) {
    const search = removeQueryStringParameter(
        currentLocation && currentLocation.search,
        queryParameterName
    );
    return {
        ...currentLocation,
        search
    };
}

function decomposeQueryStringParameters(search: typeof window.location.search) {
    const parameters = [];
    const keyValueParameters =
        search && search[0] === '?' ? search.substr(1).split('&') : [];

    for (let index = 0; index < keyValueParameters.length; index++) {
        const pair = keyValueParameters[index].split('=');
        parameters.push({
            key: decodeURIComponent(pair[0]),
            value: decodeURIComponent(pair[1])
        });
    }

    return parameters;
}

function removeQueryStringParameter(
    search: typeof window.location.search,
    key: string
) {
    const parameters = decomposeQueryStringParameters(search);
    const reducedParameters = _.filter(parameters, (pair) => pair.key !== key);
    return serialiseQueryStrinng(reducedParameters);
}

function updateQueryStringParameter(
    search: typeof window.location.search,
    key: string,
    value: string
) {
    const parameters = decomposeQueryStringParameters(search);
    const index = _.findIndex(parameters, ['key', key]);

    if (index === -1) {
        parameters.push({ key, value });
    } else {
        parameters.splice(index, 1, { key, value });
    }

    return serialiseQueryStrinng(parameters);
}

function serialiseQueryStrinng(parameters: { key: string; value: string }[]) {
    const serialisedPairs = _.map(
        parameters,
        (pair) =>
            encodeURIComponent(pair.key) + '=' + encodeURIComponent(pair.value)
    );
    const temporarySearch = '?' + serialisedPairs.join('&');
    return _.trimEnd(temporarySearch, '?');
}

export function buildEvent(
    { location, action }: { location: Location; action: any },
    disableFirstRouterChange: boolean
) {
    const impersonatingStateKey = getQueryParameter(location, 'state_key');
    const mainEvent = {
        location,
        action,
        disableFirstRouterChange
    };

    if (impersonatingStateKey) {
        return {
            ...mainEvent,
            impersonatingStateKey
        };
    }
    return mainEvent;
}

export function reloadCurrentLocation() {
    return window.location.reload();
}

export function getCurrentHostname() {
    return window.location.hostname;
}

export function getCurrentPathname() {
    return window.location.pathname;
}

export function getWindowLocation() {
    return window.location;
}

export function decomposeURLIntoLocation(partialURL: string) {
    //TODO parser instance should be a singleton in case of an a tag
    const parser = document.createElement('a');
    parser.href = partialURL;

    return parser;
}

export const maintainStateKey = (to: {
    pathname?: string;
    search?: string;
    hash?: string;
}) => {
    const currentStateKey = getQueryParameter(
        getCurrentLocation(),
        'state_key'
    );

    if (!currentStateKey) return to;

    const currentParams = to.search?.substring(1).split('&');

    if (currentParams) {
        const currentParamsWithoutStateKey = currentParams?.filter(
            (param: string) => param.split('=')[0] !== 'state_key'
        );

        const newParams = [
            ...currentParamsWithoutStateKey,
            `state_key=${currentStateKey}`
        ].join('&');

        return {
            ...to,
            search: newParams
        };
    }

    return {
        ...to,
        search: `?state_key=${currentStateKey}`
    };
};

export const buildWhiteListedSearchParams = () => {
    const params = new URLSearchParams(window.location.search);
    const hydratedSearchParams = new URLSearchParams();

    for (const WHITE_LISTED_PARAM of WHITE_LISTED_PARAMS) {
        const param = params.get(WHITE_LISTED_PARAM);
        if (param) hydratedSearchParams.append(WHITE_LISTED_PARAM, param);
    }
    return hydratedSearchParams;
};

export function isTrueLayerSuccessReturn() {
    const location = getWindowLocation();
    const statusCode = getQueryParameter(location, 'status_code');
    const referer = getQueryParameter(location, 'referer');
    return statusCode === '200' && referer === 'truelayer_callback';
}

export function isTrueLayerFailureReturn() {
    const location = getWindowLocation();
    const statusCode = getQueryParameter(location, 'status_code');
    const referer = getQueryParameter(location, 'referer');
    return (
        !!statusCode && statusCode !== '200' && referer === 'truelayer_callback'
    );
}

export function getTrueLayerErrorDetail() {
    const location = getWindowLocation();
    return getQueryParameter(location, 'error_detail');
}

export type TrueLayerCallbackResult =
    | { kind: 'success' }
    | { kind: 'cancel' }
    | { kind: 'failure' }
    | { kind: 'provider_migrated'; successorBankId?: string };

// ProviderMigrated wins over access_denied: a migrated bank's app cancels the
// user (access_denied), but we still steer them to the successor, whose bank id
// travels in error_detail.
export function getTrueLayerCallbackResult(): TrueLayerCallbackResult | null {
    const location = getWindowLocation();
    const statusCode = getQueryParameter(location, 'status_code');
    if (!statusCode) {
        return null;
    }
    if (statusCode === '200') {
        return { kind: 'success' };
    }
    if (statusCode === '500') {
        const subCode = getQueryParameter(location, 'sub_code');
        const errorDetail = getQueryParameter(location, 'error_detail');
        if (subCode === 'ProviderMigrated') {
            return { kind: 'provider_migrated', successorBankId: errorDetail };
        }
        if (errorDetail === 'access_denied') {
            return { kind: 'cancel' };
        }
    }
    return { kind: 'failure' };
}

export function clearTrueLayerParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('status_code');
    url.searchParams.delete('referer');
    url.searchParams.delete('modal');
    url.searchParams.delete('error_detail');
    url.searchParams.delete('sub_code');
    window.history.replaceState({}, '', url.toString());
}

/**
 * Builds the query string for a programmatic navigation.
 *
 * Merges, in order of increasing precedence:
 *   1. the whitelisted params preserved from the current URL (`overlay`, `state_key`)
 *   2. the params on the target path
 *   3. the supplied `stateKey` (always wins)
 *
 * Pure function — safe to unit test without React. Consumed by `useAppNavigate`.
 */
export const buildNavigationSearch = (
    targetSearch: string,
    stateKey: string
) => {
    const params = buildWhiteListedSearchParams();

    const targetParams = new URLSearchParams(targetSearch);
    targetParams.forEach((value, key) => {
        params.set(key, value);
    });

    params.set('state_key', stateKey);

    const serialised = params.toString();
    return serialised ? `?${serialised}` : '';
};
